/**
 * RicalcolaScadenzeButton — riallinea le rate di una richiesta accettata alla
 * data fattura ufficiale (senza duplicare i costi_variabili).
 */
import React from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import api from "@/api";

export default function RicalcolaScadenzeButton({ richiesta, onDone }) {
  const [open, setOpen] = React.useState(false);
  const [dataFatt, setDataFatt] = React.useState(new Date().toISOString().slice(0, 10));

  const ricalcola = useMutation({
    mutationFn: () => api.post(`/richieste-materiali/${richiesta.richiesta_id}/ricalcola-scadenze`, {
      data_riferimento: dataFatt,
      tipo: "fattura",
    }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Scadenze ricalcolate su data fattura ${dataFatt} · ${(data.rate_pagamento || []).length} rate aggiornate`);
      setOpen(false);
      onDone?.(data);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore ricalcolo"),
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Ricalcola scadenze sulla data della fattura ufficiale"
        data-testid={`btn-ricalcola-${richiesta.numero_richiesta}`}
        className="p-1 text-blue-700 hover:bg-blue-50 border border-blue-200 rounded-sm inline-flex items-center gap-1"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        <span className="text-[11px] font-semibold">Ricalcola</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-blue-200 bg-blue-50">
          <div className="text-[10px] font-bold uppercase tracking-widest text-blue-800">Ricalcola scadenze su fattura</div>
          <h3 className="font-display font-bold text-slate-900">{richiesta.numero_richiesta} · {richiesta.fornitore_nome}</h3>
        </div>
        <div className="p-4 space-y-2">
          <label className="text-xs font-semibold text-slate-600 block">Data fattura ufficiale</label>
          <input
            type="date"
            value={dataFatt}
            onChange={(e) => setDataFatt(e.target.value)}
            data-testid="input-data-fattura-ricalcola"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
          <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-900">
            ℹ️ Le rate <b>NON pagate</b> saranno riallineate alla nuova data (+giorni condizione).<br />
            Le rate <b>già pagate</b> restano invariate.<br />
            Nessuna nuova voce in Contabilità: aggiornamento in-place delle esistenti.
          </div>
        </div>
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm border border-slate-300 rounded-sm hover:bg-white">Annulla</button>
          <button
            onClick={() => ricalcola.mutate()}
            disabled={ricalcola.isPending}
            data-testid="btn-conferma-ricalcola"
            className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-sm inline-flex items-center gap-1"
          >
            <RefreshCw className={`w-4 h-4 ${ricalcola.isPending ? "animate-spin" : ""}`} />
            {ricalcola.isPending ? "…" : "Ricalcola scadenze"}
          </button>
        </div>
      </div>
    </div>
  );
}
